/* eslint-disable react-refresh/only-export-components */

import { forgetAll } from '../services/liveListStore';
import { createContext, useContext, useEffect, useState, useMemo, useRef } from "react";
import type { ReactNode } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  getAdditionalUserInfo,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  type UserCredential,
  type AuthProvider,
} from "firebase/auth";
import type { User } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase";
import { syncSessionToCMS, clearCMSSession } from '../services/cmsSession';
import { clearHandoffSession, handoffPending, handoffSettled, handoffSignedIn, supersededByHandoff } from '../handoff';
import type {
  UserProfile,
  AccountType,
  ProfileMode,
  PlayerProfile,
  CoachProfile,
  UpdatePlayerProfileData,
  UpdateCoachProfileData,
} from "../types/user";
import {
  getUserProfile,
  createUserProfile,
  updateUserProfile as updateUserProfileService,
  updatePlayerProfile as updatePlayerProfileService,
  updateCoachProfile as updateCoachProfileService,
  switchProfileMode as switchProfileModeService,
  initializeCoachProfile,
  requestCoachVerification,
  completeCoachOnboarding as completeCoachOnboardingService,
} from "../services/userProfile";
import { applyStoredReferral } from "../services/referrals";
import { useNavigate } from "react-router-dom";

interface AuthContextType {
  // User and profile state
  currentUser: User | null;
  userProfile: UserProfile | null;
  loading: boolean;

  // Profile accessors
  playerProfile: PlayerProfile | null;
  coachProfile: CoachProfile | null;
  currentMode: ProfileMode;
  activeProfile: PlayerProfile | CoachProfile | null;

  // Computed values
  hasCoachProfile: boolean;
  isCoachVerified: boolean;
  isCoachPending: boolean;
  canSwitchToCoach: boolean;
  emailVerified: boolean;

  // Role-based access
  isAdmin: boolean;              // role === 'admin' || role === 'root'
  isRoot: boolean;               // role === 'root'
  isStaff: boolean;              // any internal staff role
  hasAdminPanelAccess: boolean;  // can enter the admin panel (admin, root, sales, accounts)

  // Auth functions
  signup: (email: string, password: string, displayName: string, accountType: AccountType) => Promise<UserCredential>;
  login: (email: string, password: string) => Promise<UserCredential>;
  logout: () => Promise<void>;
  loginWithGoogle: (mode?: 'login' | 'signup') => Promise<{ credential: UserCredential; isNewUser: boolean }>;
  loginWithApple: (mode?: 'login' | 'signup') => Promise<{ credential: UserCredential; isNewUser: boolean }>;

  // Auth action lock — prevents onAuthStateChanged from overwriting profile
  // during external signup flows (e.g. InviteSignup)
  acquireAuthLock: () => void;
  releaseAuthLock: () => void;

  // Profile management
  refreshUserProfile: (forceServer?: boolean) => Promise<void>;
  loadProfileForUid: (uid: string) => Promise<void>;
  updateProfile: (data: Partial<UserProfile>) => Promise<void>;
  updatePlayerProfile: (data: UpdatePlayerProfileData) => Promise<void>;
  updateCoachProfile: (data: UpdateCoachProfileData) => Promise<void>;

  // Profile switching
  switchMode: (mode: ProfileMode) => Promise<void>;

  // Coach application flow
  applyAsCoach: () => Promise<void>;
  submitCoachVerification: () => Promise<void>;
  completeCoachOnboarding: () => Promise<void>;

  // Password management
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  /** Reauthenticate only, for checking a password without changing anything. */
  verifyPassword: (currentPassword: string) => Promise<void>;
  hasPasswordProvider: boolean;
  hasGoogleProvider: boolean;
  hasAppleProvider: boolean;

  // Email verification
  markEmailVerified: () => void;

  // Logout flow
  handleLogout: () => Promise<void>;
}

// Instagram, Facebook, Twitter/X, Line, and generic WKWebView all block
// signInWithPopup. Detect these contexts and fall back to redirect flow.
function isInAppBrowser(): boolean {
  const ua = navigator.userAgent;
  return /FBAN|FBAV|Instagram|Twitter|Line/.test(ua) ||
    (/iPhone|iPad/.test(ua) && !/Safari\//.test(ua));
}

// Stores the sign-in mode ('login' | 'signup') across the redirect round-trip.
const REDIRECT_MODE_KEY = 'db_oauthRedirectMode';
const REDIRECT_PROVIDER_KEY = 'db_oauthRedirectProvider';

/**
 * Error codes that mean the user deliberately backed out, not that anything
 * failed. `popup-closed-by-user` is the window being closed;
 * `cancelled-popup-request` is the older of two popups when a second is
 * opened. Neither deserves a message: the user already knows what they did.
 */
export function isPopupCancellation(err: unknown): boolean {
  const code = (err as { code?: string } | null)?.code;
  return code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request';
}

/**
 * How long the sign-in promise gets to settle once the popup window is gone.
 *
 * A closed popup is not proof of cancellation — Firebase closes it itself on
 * success, and the credential can still be in flight through the auth iframe
 * at that moment. Waiting this long lets a successful sign-in win the race.
 */
const POPUP_SETTLE_GRACE_MS = 1200;

/** How often the popup handle is checked. Cheap; `closed` is a plain flag. */
const POPUP_POLL_MS = 250;

/**
 * `signInWithPopup`, but rejecting promptly when the user closes the popup.
 *
 * Firebase's own detection polls for a closed window on a slow cadence: a
 * cancelled Google sign-in took a measured 8.6s to reject, and the page sits
 * inert for all of it. There is no option to shorten that, so the window is
 * watched here instead, which brings the same rejection back to roughly 1.5s.
 * That is when the rejection fires, not when the user sees it: the spinner
 * still clears only on the next click. See the note on the listeners below.
 *
 * Firebase owns the popup, so the handle is captured by wrapping
 * `window.open` for the duration of the call. The wrapper cannot be removed
 * synchronously — `signInWithPopup` builds the handler URL behind an `await`
 * and opens the window after its first suspension point — so it is removed
 * once the window arrives, and on a timeout in case none ever does.
 */
function signInWithPopupCancellable(
  provider: AuthProvider & { providerId: string },
): Promise<UserCredential> {
  const nativeOpen = window.open;
  let popup: Window | null = null;

  const patched: typeof window.open = (...args) => {
    const win = nativeOpen.apply(window, args);
    if (win) {
      popup = win;
      restore();
    }
    return win;
  };
  // Only ever removes its own wrapper, so a second sign-in started while this
  // one is still watching cannot restore over the top of it.
  function restore() {
    if (window.open === patched) window.open = nativeOpen;
  }
  window.open = patched;
  const openTimeout = setTimeout(restore, 10_000);

  const signIn = signInWithPopup(auth, provider);

  return new Promise<UserCredential>((resolve, reject) => {
    let settled = false;
    let graceStarted = false;

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearInterval(poll);
      clearTimeout(openTimeout);
      window.removeEventListener('focus', checkClosed);
      document.removeEventListener('visibilitychange', checkClosed);
      restore();
      fn();
    };

    /**
     * Starts the grace period once the popup is gone. Idempotent, so the poll
     * and both listeners can all call it without stacking timers.
     *
     * The `popup.closed` test is what makes the listeners safe: returning to
     * the page while the popup is still open must do nothing at all, or
     * alt-tabbing back mid-sign-in would cancel a live attempt.
     */
    const checkClosed = () => {
      if (graceStarted || settled || !popup?.closed) return;
      graceStarted = true;
      clearInterval(poll);
      // Firebase's own promise stays live. Give it the grace period to land a
      // real result before calling this a cancellation.
      setTimeout(
        () =>
          finish(() =>
            reject(
              Object.assign(new Error('Sign-in popup closed.'), {
                code: 'auth/popup-closed-by-user',
              }),
            ),
          ),
        POPUP_SETTLE_GRACE_MS,
      );
    };

    const poll = setInterval(checkClosed, POPUP_POLL_MS);

    // Insurance against a throttled poll: browsers slow timers on a hidden
    // page — Chrome to roughly once a second, Safari harder still, and a
    // standalone PWA sits backgrounded behind the popup — so the tick that
    // notices the closed window can be deferred. Closing the popup returns
    // focus and visibility to the opener, so these fire on their own. They
    // start the same grace period rather than bypassing it: on a successful
    // sign-in the popup also closes and focus also returns, at the same moment
    // the credential arrives, and the credential must win.
    //
    // Do not assume these fixed the symptom they were added for. They did not.
    // The spinner still clears only when the user clicks the page, and adding
    // both listeners changed nothing, which rules throttling out as the cause.
    // The detection itself is not the problem: it fires. What has not been
    // chased down is why the resulting state change does not reach the screen
    // until an interaction, and a deferred React flush is the likely culprit.
    // Look there before touching anything above.
    window.addEventListener('focus', checkClosed);
    document.addEventListener('visibilitychange', checkClosed);

    signIn.then(
      (cred) => finish(() => resolve(cred)),
      (err) => finish(() => reject(err)),
    );
  });
}

async function signInWithBestMethod(
  provider: AuthProvider & { providerId: string },
  mode: 'login' | 'signup',
): Promise<UserCredential | null> {
  if (isInAppBrowser()) {
    sessionStorage.setItem(REDIRECT_MODE_KEY, mode);
    sessionStorage.setItem(REDIRECT_PROVIDER_KEY, provider.providerId);
    await signInWithRedirect(auth, provider);
    return null; // page navigates away
  }
  return signInWithPopupCancellable(provider);
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Ref to prevent onAuthStateChanged from racing with signup/login
  const authActionInProgress = useRef(false);

  // Ref to persist email verification across profile re-fetches within this session.
  // Prevents stale Firestore cache reads from overwriting the verified status.
  const emailVerifiedThisSession = useRef(false);

  // Tracks rejection paths that called signOut/delete before finally ran.
  // Keeps authActionInProgress true until onAuthStateChanged(null) confirms sign-out,
  // preventing a queued onAuthStateChanged(user) from loading the rejected user's profile.
  const signOutInFlight = useRef(false);

  // Computed values from userProfile
  const playerProfile = useMemo(() => userProfile?.playerProfile || null, [userProfile]);
  const coachProfile = useMemo(() => userProfile?.coachProfile || null, [userProfile]);
  const currentMode = useMemo<ProfileMode>(() =>
    (userProfile?.currentMode || userProfile?.accountType || 'player') as ProfileMode,
    [userProfile]
  );

  const activeProfile = useMemo(() => {
    if (!userProfile) return null;
    return currentMode === 'coach' ? coachProfile : playerProfile;
  }, [userProfile, currentMode, coachProfile, playerProfile]);

  const hasCoachProfile = useMemo(() => !!coachProfile, [coachProfile]);

  const isCoachVerified = useMemo(() =>
    coachProfile?.verificationStatus === 'verified',
    [coachProfile]
  );

  const isCoachPending = useMemo(() =>
    coachProfile?.verificationStatus === 'pending',
    [coachProfile]
  );

  const canSwitchToCoach = useMemo(() =>
    hasCoachProfile && isCoachVerified,
    [hasCoachProfile, isCoachVerified]
  );

  const hasPasswordProvider = useMemo(() => {
    return currentUser?.providerData?.some(p => p.providerId === 'password') ?? false;
  }, [currentUser]);

  const hasGoogleProvider = useMemo(() => {
    return currentUser?.providerData?.some(p => p.providerId === 'google.com') ?? false;
  }, [currentUser]);

  const hasAppleProvider = useMemo(() => {
    return currentUser?.providerData?.some(p => p.providerId === 'apple.com') ?? false;
  }, [currentUser]);

  // emailVerified is true only when explicitly set to true in the profile.
  // Once verified this session, stays true even if stale cache overwrites profile.
  const emailVerified = useMemo(() => {
    if (!userProfile) return true;
    if (emailVerifiedThisSession.current) return true;
    return userProfile.emailVerified === true;
  }, [userProfile]);

  // Role-based access — derived solely from the `role` field
  const isRoot = useMemo(() => userProfile?.role === 'root', [userProfile]);
  const isAdmin = useMemo(() =>
    userProfile?.role === 'root' || userProfile?.role === 'admin',
    [userProfile]
  );
  const hasAdminPanelAccess = useMemo(() =>
    ['root', 'admin', 'sales', 'accounts'].includes(userProfile?.role ?? ''),
    [userProfile]
  );
  const isStaff = useMemo(() =>
    ['root', 'admin', 'support', 'sales', 'accounts'].includes(userProfile?.role ?? ''),
    [userProfile]
  );

  /**
   * The reauthenticate half of `changePassword`, on its own.
   *
   * The account screen checks the current password before unlocking the new
   * ones, so it needs a way to ask "is this right?" without committing to a
   * change. `changePassword` deliberately still does its own reauthenticate:
   * this is an early check for the user's benefit, never the gate.
   */
  async function verifyPassword(currentPassword: string) {
    if (!currentUser || !currentUser.email) throw new Error('Not authenticated');
    const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
    await reauthenticateWithCredential(currentUser, credential);
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    if (!currentUser || !currentUser.email) throw new Error('Not authenticated');
    const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
    await reauthenticateWithCredential(currentUser, credential);
    await updatePassword(currentUser, newPassword);
  }

  async function signup(email: string, password: string, displayName: string, accountType: AccountType) {
    authActionInProgress.current = true;
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Create user profile in Firestore with embedded profiles
      await createUserProfile({
        uid: userCredential.user.uid,
        email: userCredential.user.email || email,
        displayName,
        accountType,
        emailVerified: false,
      });
      if (accountType === 'player') await applyStoredReferral(userCredential.user.uid);

      // Fetch the newly created profile
      const profile = await getUserProfile(userCredential.user.uid);
      setUserProfile(profile);

      return userCredential;
    } finally {
      authActionInProgress.current = false;
    }
  }

  async function login(email: string, password: string) {
    authActionInProgress.current = true;
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Fetch user profile (force server read to get latest emailVerified status)
      const profile = await getUserProfile(userCredential.user.uid, true);
      if (profile?.isActive === false) {
        signOutInFlight.current = true;
        await signOut(auth);
        throw new Error('Your account has been deactivated. Please contact support.');
      }
      setUserProfile(profile);

      return userCredential;
    } finally {
      if (!signOutInFlight.current) authActionInProgress.current = false;
    }
  }

  async function logout() {
    emailVerifiedThisSession.current = false;
    setUserProfile(null);
    // The tab's kept handoff goes too, so a reload doesn't sign back in.
    clearHandoffSession();
    return signOut(auth);
  }

  async function loginWithGoogle(mode: 'login' | 'signup' = 'login') {
    authActionInProgress.current = true;
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');

      const result = await signInWithBestMethod(provider, mode);
      if (!result) return { credential: null as unknown as UserCredential, isNewUser: false };
      const userCredential = result;
      const isNewUser = !!(getAdditionalUserInfo(userCredential)?.isNewUser);

      let profile = await getUserProfile(userCredential.user.uid);

      if (mode === 'login' && !profile) {
        // Firebase Auth created a new account via the popup — delete it so no
        // orphaned Auth record is left behind. If the Auth account already existed
        // (isNewUser === false) it is pre-existing; just sign out.
        signOutInFlight.current = true;
        if (isNewUser) {
          await userCredential.user.delete();
        } else {
          await signOut(auth);
        }
        throw Object.assign(
          new Error('No account found with this Google account. Please sign up first.'),
          { code: 'auth/user-not-found-in-db' }
        );
      }

      if (mode === 'signup' && profile) {
        signOutInFlight.current = true;
        await signOut(auth);
        throw Object.assign(
          new Error('An account with this Google account already exists. Please log in instead.'),
          { code: 'auth/account-exists-in-db' }
        );
      }

      if (!profile) {
        await createUserProfile({
          uid: userCredential.user.uid,
          email: userCredential.user.email || '',
          displayName: userCredential.user.displayName || userCredential.user.email?.split('@')[0],
          accountType: 'player',
          emailVerified: true,
        });
        await applyStoredReferral(userCredential.user.uid);
        profile = await getUserProfile(userCredential.user.uid);
      }

      if (profile?.isActive === false) {
        signOutInFlight.current = true;
        await signOut(auth);
        throw new Error('Your account has been deactivated. Please contact support.');
      }
      setUserProfile(profile);
      return { credential: userCredential, isNewUser };
    } finally {
      if (!signOutInFlight.current) authActionInProgress.current = false;
    }
  }

  async function loginWithApple(mode: 'login' | 'signup' = 'login') {
    authActionInProgress.current = true;
    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');

      const result = await signInWithBestMethod(provider, mode);
      if (!result) return { credential: null as unknown as UserCredential, isNewUser: false };
      const userCredential = result;
      const isNewUser = !!(getAdditionalUserInfo(userCredential)?.isNewUser);

      let profile = await getUserProfile(userCredential.user.uid);

      if (mode === 'login' && !profile) {
        signOutInFlight.current = true;
        if (isNewUser) {
          await userCredential.user.delete();
        } else {
          await signOut(auth);
        }
        throw Object.assign(
          new Error('No account found with this Apple ID. Please sign up first.'),
          { code: 'auth/user-not-found-in-db' }
        );
      }

      if (mode === 'signup' && profile) {
        signOutInFlight.current = true;
        await signOut(auth);
        throw Object.assign(
          new Error('An account with this Apple ID already exists. Please log in instead.'),
          { code: 'auth/account-exists-in-db' }
        );
      }

      if (!profile) {
        await createUserProfile({
          uid: userCredential.user.uid,
          email: userCredential.user.email || '',
          displayName: userCredential.user.displayName || userCredential.user.email?.split('@')[0],
          accountType: 'player',
          emailVerified: true,
        });
        await applyStoredReferral(userCredential.user.uid);
        profile = await getUserProfile(userCredential.user.uid);
      }

      if (profile?.isActive === false) {
        signOutInFlight.current = true;
        await signOut(auth);
        throw new Error('Your account has been deactivated. Please contact support.');
      }
      setUserProfile(profile);
      return { credential: userCredential, isNewUser };
    } finally {
      if (!signOutInFlight.current) authActionInProgress.current = false;
    }
  }

  async function refreshUserProfile(forceServer: boolean = false) {
    if (currentUser) {
      const profile = await getUserProfile(currentUser.uid, forceServer);
      setUserProfile(profile);
    }
  }

  async function loadProfileForUid(uid: string) {
    const profile = await getUserProfile(uid, true);
    setUserProfile(profile);
  }

  function markEmailVerified() {
    emailVerifiedThisSession.current = true;
    setUserProfile((prev) => prev ? { ...prev, emailVerified: true } : prev);
  }

  /**
   * Legacy update profile function - updates root level fields
   */
  async function updateProfile(data: Partial<UserProfile>) {
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    await updateUserProfileService(currentUser.uid, data);
    await refreshUserProfile(true);
  }

  /**
   * Update the player profile specifically
   */
  async function updatePlayerProfile(data: UpdatePlayerProfileData) {
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    await updatePlayerProfileService(currentUser.uid, data);
    await refreshUserProfile();
  }

  /**
   * Update the coach profile specifically
   */
  async function updateCoachProfile(data: UpdateCoachProfileData) {
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    await updateCoachProfileService(currentUser.uid, data);
    await refreshUserProfile(true);
  }

  /**
   * Switch between coach and player modes.
   * Only verified coaches can switch to coach mode.
   */
  async function switchMode(mode: ProfileMode) {
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    await switchProfileModeService(currentUser.uid, mode);
    // Force server read to ensure we get the updated mode
    await refreshUserProfile(true);
  }

  /**
   * Initialize an empty coach profile for a player who wants to become a coach.
   * This is the first step before going through coach onboarding.
   */
  async function applyAsCoach() {
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    if (hasCoachProfile) {
      throw new Error('Coach profile already exists');
    }

    await initializeCoachProfile(currentUser.uid);
    await refreshUserProfile();
  }

  /**
   * Completes coach onboarding for invitation-only coaches.
   * Sets verificationStatus to 'verified' immediately and keeps currentMode as 'coach'.
   * Called from step-3's Go-live submit handler — no admin review required.
   */
  async function completeCoachOnboarding() {
    if (!currentUser) throw new Error('No user logged in');
    if (!hasCoachProfile) throw new Error('No coach profile exists');
    await completeCoachOnboardingService(currentUser.uid);
    await refreshUserProfile(true);
  }

  /**
   * Submit coach profile for verification after completing onboarding.
   * Sets verificationStatus to 'pending'.
   */
  async function submitCoachVerification() {
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    if (!hasCoachProfile) {
      throw new Error('No coach profile exists');
    }

    await requestCoachVerification(currentUser.uid);
    // Force server read to ensure we get the updated verification status
    await refreshUserProfile(true);
  }

  async function handleLogout() {
    // Nothing of one account's kept lists shows for the next.
    forgetAll();
    try {
      await logout();
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 100);
    } catch (error) {
      console.error("Failed to log out", error);
    }
  }

  // Handle the OAuth redirect result when the user returns from Google/Apple sign-in.
  // Runs once on mount; getRedirectResult resolves immediately with null when there
  // is no pending redirect so it is safe to call on every page load.
  useEffect(() => {
    getRedirectResult(auth).then(async (result) => {
      if (!result) return;
      const mode = (sessionStorage.getItem(REDIRECT_MODE_KEY) || 'login') as 'login' | 'signup';
      sessionStorage.removeItem(REDIRECT_MODE_KEY);
      sessionStorage.removeItem(REDIRECT_PROVIDER_KEY);

      const isNewUser = !!(getAdditionalUserInfo(result)?.isNewUser);
      let profile = await getUserProfile(result.user.uid);

      if (mode === 'login' && !profile) {
        signOutInFlight.current = true;
        if (isNewUser) await result.user.delete(); else await signOut(auth);
        throw Object.assign(new Error('No account found. Please sign up first.'), { code: 'auth/user-not-found-in-db' });
      }
      if (mode === 'signup' && profile) {
        signOutInFlight.current = true;
        await signOut(auth);
        throw Object.assign(new Error('An account already exists. Please log in instead.'), { code: 'auth/account-exists-in-db' });
      }
      if (!profile) {
        await createUserProfile({
          uid: result.user.uid,
          email: result.user.email || '',
          displayName: result.user.displayName || result.user.email?.split('@')[0],
          accountType: 'player',
          emailVerified: true,
        });
        await applyStoredReferral(result.user.uid);
        profile = await getUserProfile(result.user.uid);
      }
      if (profile?.isActive === false) {
        signOutInFlight.current = true;
        await signOut(auth);
        throw new Error('Your account has been deactivated. Please contact support.');
      }
      setUserProfile(profile);
    }).catch((err) => {
      console.error('[AuthContext] redirect result error:', err);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let unsubscribeProfile: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      // The native sign-in handoff is still signing in: not signed out yet,
      // and not whoever the web view had before (30 Oct). Its sign-in calls
      // back here with the user; failed, the web view's own session, or
      // signed out, carries on.
      if (handoffPending()) {
        await handoffSettled();
        if (supersededByHandoff(user?.uid ?? null, auth.currentUser?.uid ?? null)) return;
      }
      // Clean up previous profile listener
      if (unsubscribeProfile) {
        unsubscribeProfile();
        unsubscribeProfile = null;
      }

      setCurrentUser(user);
      // A handoff's session (2 Nov): the route renders now, from the signed-in state; the profile
      // follows without holding it.
      if (user && handoffSignedIn()) setLoading(false);

      if (user) {
        // Sync session cookie to CMS (fire-and-forget)
        syncSessionToCMS(user).catch(() => {});

        // Skip profile handling if signup/login is managing it
        if (authActionInProgress.current) {
          setLoading(false);
          // Still set up deactivation listener
          unsubscribeProfile = onSnapshot(doc(db, 'users', user.uid), (snapshot) => {
            // Only act on server-confirmed data — skip optimistic local writes so a
            // self-initiated account deletion can finish writing before signOut fires.
            if (snapshot.exists() && snapshot.data().isActive === false && !snapshot.metadata.hasPendingWrites) {
              signOut(auth);
            }
          });
          return;
        }

        // Fetch user profile from cache first so the app unblocks immediately;
        // the real-time onSnapshot listener below will deliver any fresher data.
        let profile: UserProfile | null = null;
        try {
          profile = await getUserProfile(user.uid, false);
        } catch {
          try { profile = await getUserProfile(user.uid, true); } catch { /* ignore */ }
        }

        // If profile doesn't exist, create one (for legacy users)
        if (!profile) {
          await createUserProfile({
            uid: user.uid,
            email: user.email || '',
            displayName: user.displayName || user.email?.split('@')[0],
            accountType: 'player',
          });
          profile = await getUserProfile(user.uid);
        }

        // Ensure displayName exists (migration for old profiles). Only fills in
        // a name the provider actually gave us. A blank displayName is now the
        // normal pre-onboarding state for an email signup, so deriving one from
        // the email address here would put a manufactured name straight back
        // into the field onboarding step 1 exists to collect.
        if (profile && !profile.displayName && user.displayName) {
          await updateUserProfileService(user.uid, { displayName: user.displayName });
          profile = await getUserProfile(user.uid);
        }

        // Check if already deactivated
        if (profile?.isActive === false) {
          await signOut(auth);
          setUserProfile(null);
          setLoading(false);
          return;
        }

        setUserProfile(profile);

        // Real-time listener for deactivation while logged in
        unsubscribeProfile = onSnapshot(doc(db, 'users', user.uid), (snapshot) => {
          // Only act on server-confirmed data — skip optimistic local writes so a
          // self-initiated account deletion can finish writing before signOut fires.
          if (snapshot.exists() && snapshot.data().isActive === false && !snapshot.metadata.hasPendingWrites) {
            signOut(auth);
          }
        });
      } else {
        // Signed out — clear any held locks from rejection paths
        authActionInProgress.current = false;
        signOutInFlight.current = false;
        setUserProfile(null);
        // Clear CMS session cookie (fire-and-forget)
        clearCMSSession().catch(() => {});
      }

      setLoading(false);
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeProfile) {
        unsubscribeProfile();
      }
    };
  }, []);

  const value: AuthContextType = {
    // User and profile state
    currentUser,
    userProfile,
    loading,

    // Profile accessors
    playerProfile,
    coachProfile,
    currentMode,
    activeProfile,

    // Computed values
    hasCoachProfile,
    isCoachVerified,
    isCoachPending,
    canSwitchToCoach,
    emailVerified,
    isAdmin,
    isRoot,
    isStaff,
    hasAdminPanelAccess,

    // Auth functions
    signup,
    login,
    logout,
    loginWithGoogle,
    loginWithApple,

    // Profile management
    refreshUserProfile,
    loadProfileForUid,
    updateProfile,
    updatePlayerProfile,
    updateCoachProfile,

    // Profile switching
    switchMode,

    // Auth action lock
    acquireAuthLock: () => { authActionInProgress.current = true; },
    releaseAuthLock: () => { authActionInProgress.current = false; },

    // Coach application flow
    applyAsCoach,
    submitCoachVerification,
    completeCoachOnboarding,

    // Password management
    changePassword,
    verifyPassword,
    hasPasswordProvider,
    hasGoogleProvider,
    hasAppleProvider,

    // Email verification
    markEmailVerified,

    // Logout flow
    handleLogout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
